export const DASHBOARD_APPS_CACHE_PREFIX = 'ss_dashboard_apps_cache_v1:';
export const PIPELINE_STATS_CACHE_PREFIX = 'ss_pipeline_stats_cache_v1:';
export const CACHE_APP_LIMIT = 1000;
const LAST_RECOVERY_KEY = 'ss_last_recovery_action_v1';

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function storageKey(prefix, key = 'anonymous') {
  return `${prefix}${key || 'anonymous'}`;
}

export function recordRecoveryAction(action, result = {}) {
  const ls = storage();
  if (!ls) return;
  try {
    ls.setItem(LAST_RECOVERY_KEY, JSON.stringify({
      action,
      at: new Date().toISOString(),
      result,
    }));
  } catch {
    // Best-effort only.
  }
}

export function getLastRecoveryAction() {
  try {
    return safeParse(storage()?.getItem(LAST_RECOVERY_KEY));
  } catch {
    return null;
  }
}

export function readCachedDashboardApps(userId) {
  const ls = storage();
  if (!ls) return null;
  const cached = safeParse(ls.getItem(storageKey(DASHBOARD_APPS_CACHE_PREFIX, userId)));
  if (!cached || !Array.isArray(cached.apps)) return null;
  return cached;
}

export function writeCachedDashboardApps(userId, apps) {
  if (!userId || !Array.isArray(apps) || apps.length === 0) return;
  const ls = storage();
  if (!ls) return;
  try {
    ls.setItem(storageKey(DASHBOARD_APPS_CACHE_PREFIX, userId), JSON.stringify({
      cached_at: new Date().toISOString(),
      limited: apps.length > CACHE_APP_LIMIT,
      apps: apps.slice(0, CACHE_APP_LIMIT),
    }));
  } catch {
    // localStorage is best-effort only; never break the app over quota/private mode.
  }
}

export function pipelineStatsCacheKey(userId, councils, period) {
  const councilKey = Array.isArray(councils) && councils.length ? councils.join('|') : 'all';
  return `${userId || 'anonymous'}:${councilKey}:${Number(period) || 0}`;
}

export function readCachedPipelineStats(key) {
  const ls = storage();
  if (!ls) return null;
  const cached = safeParse(ls.getItem(storageKey(PIPELINE_STATS_CACHE_PREFIX, key)));
  if (!cached || !cached.stats) return null;
  return cached;
}

export function writeCachedPipelineStats(key, stats) {
  if (!key || !stats || stats.load_error) return;
  const ls = storage();
  if (!ls) return;
  try {
    ls.setItem(storageKey(PIPELINE_STATS_CACHE_PREFIX, key), JSON.stringify({
      cached_at: new Date().toISOString(),
      stats,
    }));
  } catch {
    // Best-effort cache.
  }
}

function isSiteScopeStorageName(name) {
  return /sitescope|supabase|workbox/i.test(String(name || ''));
}

async function clearSiteScopeCaches() {
  if (typeof window === 'undefined' || !('caches' in window)) return 0;
  const names = await caches.keys();
  const matchingNames = names.filter(isSiteScopeStorageName);
  await Promise.all(matchingNames.map(name => caches.delete(name)));
  return matchingNames.length;
}

async function unregisterServiceWorkers() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 0;
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map(reg => reg.unregister()));
  return registrations.length;
}

async function clearSiteScopeIndexedDb() {
  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') return 0;
  const databases = await indexedDB.databases();
  const matchingNames = databases
    .map(db => db.name)
    .filter(isSiteScopeStorageName);
  await Promise.all(matchingNames.map(name => new Promise(resolve => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = resolve;
    request.onerror = resolve;
    request.onblocked = resolve;
  })));
  return matchingNames.length;
}

function clearWebStorage() {
  const cleared = [];
  try {
    window.localStorage?.clear();
    cleared.push('localStorage');
  } catch {
    // Best effort only; Safari private mode can throw here.
  }
  try {
    window.sessionStorage?.clear();
    cleared.push('sessionStorage');
  } catch {
    // Best effort only.
  }
  return cleared;
}

export async function repairAppShell() {
  const [cachesCleared, serviceWorkersRemoved] = await Promise.all([
    clearSiteScopeCaches(),
    unregisterServiceWorkers(),
  ]);
  const result = { cachesCleared, serviceWorkersRemoved };
  recordRecoveryAction('repair_app_shell', result);
  return result;
}

export async function resetAppOnThisDevice() {
  const result = {
    cachesCleared: 0,
    serviceWorkersRemoved: 0,
    indexedDbsCleared: 0,
    storageCleared: [],
  };

  try {
    const [cachesCleared, serviceWorkersRemoved, indexedDbsCleared] = await Promise.all([
      clearSiteScopeCaches(),
      unregisterServiceWorkers(),
      clearSiteScopeIndexedDb(),
    ]);
    result.cachesCleared = cachesCleared;
    result.serviceWorkersRemoved = serviceWorkersRemoved;
    result.indexedDbsCleared = indexedDbsCleared;
    result.storageCleared = clearWebStorage();
    recordRecoveryAction('reset_app_on_this_device', result);
  } catch (error) {
    console.warn('[SiteScope] Device reset hit a recoverable error:', error);
    recordRecoveryAction('reset_app_on_this_device_failed', {
      message: error?.message || String(error || 'Unknown reset error'),
      ...result,
    });
  }

  return result;
}

export async function refreshAppShell() {
  try {
    await repairAppShell();
  } catch (error) {
    console.warn('[SiteScope] App recovery failed before reload:', error);
  }
  window.location.reload();
}
